import {observer} from 'mobx-react-lite';
import React from 'react';
import {EUndecoratedButton} from 'webapp-lib/Components';

import i18n from '_/i18n';
import {Color} from '_/utilities/types';

import Tag from '../Tag';

import {LabelWrapper, StyledTagsGroup, TagsWrapper} from './styles';

export type BasicTagItem = {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  callback?: (arg0: boolean) => void;
  closeCallback?: () => void;
  color: Color;
  state: boolean;
  text: string;
  id?: string; // default value for id is text value
};

export type AlternativeTagItem = {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  callback?: (arg0: boolean) => void;
  closeCallback?: () => void;
  text: string;
  id?: string; // default value for id is text value
};

type BasicProps = {
  alternativeStyle?: false;
  className?: string;
  disabled?: boolean;
  label: string | React.ReactNode;
  fitToMultiLines?: boolean;
  tags: BasicTagItem[];
};

type AlternativeProps = {
  alternativeStyle: true;
  className?: string;
  disabled?: boolean;
  label: string | React.ReactNode;
  fitToMultiLines?: boolean;
  tags: AlternativeTagItem[];
};

type ClearProps = {
  showClear?: boolean;
  clearButtonDisabled?: boolean;
  onClear?: () => void;
};

type Props = (BasicProps | AlternativeProps) & ClearProps;

const UTagsGroup = ({
  alternativeStyle = false,
  disabled,
  label,
  tags,
  fitToMultiLines,
  showClear = false,
  clearButtonDisabled = false,
  onClear = () => {},
}: Props): React.ReactElement => (
  <StyledTagsGroup className='TagsGroup'>
    <LabelWrapper>
      <span>{label}</span>
      {showClear ? (
        <EUndecoratedButton
          disabled={clearButtonDisabled}
          textColor
          onClick={onClear}
        >
          {i18n.t('common:actions.clearSelection')}
        </EUndecoratedButton>
      ) : null}
    </LabelWrapper>
    {alternativeStyle ? (
      <TagsWrapper>
        {(tags as AlternativeTagItem[]).map(
          ({text, id, callback, closeCallback, ...props}, index) => (
            <Tag
              key={id ?? text ?? index}
              fitToMultiLines={fitToMultiLines}
              tagName={text}
              alternativeStyle
              onChange={disabled ? undefined : callback}
              onClose={disabled ? undefined : closeCallback}
              {...props}
            />
          )
        )}
      </TagsWrapper>
    ) : (
      <TagsWrapper>
        {(tags as BasicTagItem[]).map(
          ({text, state, id, callback, closeCallback, ...props}) => (
            <Tag
              key={id || text}
              checked={state}
              fitToMultiLines={fitToMultiLines}
              tagName={text}
              onChange={disabled ? undefined : callback}
              onClose={disabled ? undefined : closeCallback}
              {...props}
            />
          )
        )}
      </TagsWrapper>
    )}
  </StyledTagsGroup>
);

export const TagsGroup = observer(UTagsGroup);
